import { useState, useCallback, useRef } from 'react';
import Papa from 'papaparse';
import { useApp } from '../context/AppContext';
import { BROKER_CSV_FIELDS, PRICE_CSV_FIELDS } from '../utils/constants';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Upload, FileText, Check, X, AlertTriangle } from 'lucide-react';
import PdfImport from './PdfImport';

export default function UploadModule() {
  const { setBrokerData, setPriceData, brokerData, priceData } = useApp();
  const [activeTab, setActiveTab] = useState('broker');

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="upload-module">
      <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Data Import</h1>
      <p className="text-sm text-trading-muted mb-6">Upload broker activity, price data, or Philstocks PDF reports</p>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-trading-elevated border border-trading-border">
          <TabsTrigger value="broker" data-testid="tab-broker" className="data-[state=active]:bg-trading-surface data-[state=active]:text-trading-text text-trading-muted text-sm">
            Broker CSV
          </TabsTrigger>
          <TabsTrigger value="price" data-testid="tab-price" className="data-[state=active]:bg-trading-surface data-[state=active]:text-trading-text text-trading-muted text-sm">
            Price CSV
          </TabsTrigger>
          <TabsTrigger value="pdf" data-testid="tab-pdf" className="data-[state=active]:bg-trading-surface data-[state=active]:text-trading-text text-trading-muted text-sm">
            PDF Import
          </TabsTrigger>
        </TabsList>

        <TabsContent value="broker" className="mt-4">
          <CsvUploader
            type="broker"
            fields={BROKER_CSV_FIELDS}
            label="broker activity"
            onImport={(data) => setBrokerData(prev => [...prev, ...data])}
          />
        </TabsContent>

        <TabsContent value="price" className="mt-4">
          <CsvUploader
            type="price"
            fields={PRICE_CSV_FIELDS}
            label="price data"
            onImport={(data) => setPriceData(prev => [...prev, ...data])}
          />
        </TabsContent>

        <TabsContent value="pdf" className="mt-4">
          <PdfImport />
        </TabsContent>
      </Tabs>

      {/* Data summary */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <DataSummaryCard
          title="Broker Activity Data"
          count={brokerData.length}
          symbols={[...new Set(brokerData.map(r => r.symbol))].length}
          onClear={() => setBrokerData([])}
          testId="broker-data-summary"
        />
        <DataSummaryCard
          title="Price Data"
          count={priceData.length}
          symbols={[...new Set(priceData.map(r => r.symbol))].length}
          onClear={() => setPriceData([])}
          testId="price-data-summary"
        />
      </div>
    </div>
  );
}

function CsvUploader({ type, fields, label, onImport }) {
  const [rawData, setRawData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [fileName, setFileName] = useState('');
  const fileRef = useRef(null);

  const handleFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        const cols = results.meta.fields || [];
        setRawData(data);
        setHeaders(cols);
        setPreview(data.slice(0, 5));
        const auto = {};
        fields.forEach(f => {
          const exact = cols.find(h => h.toLowerCase().replace(/[_\s-]/g, '') === f.key.toLowerCase());
          if (exact) { auto[f.key] = exact; return; }
          const partial = cols.find(h => h.toLowerCase().includes(f.key.toLowerCase()) || f.key.toLowerCase().includes(h.toLowerCase().replace(/[_\s-]/g, '')));
          if (partial) auto[f.key] = partial;
        });
        setMapping(auto);
      },
    });
    e.target.value = '';
  }, [fields]);

  const handleConfirm = () => {
    if (!rawData) return;
    const numericFields = ['buyVol', 'buyAmt', 'buyAve', 'sellVol', 'sellAmt', 'sellAve', 'netAmt', 'totalValue', 'open', 'high', 'low', 'close', 'volume', 'value'];
    const normalized = rawData
      .map(row => {
        const obj = {};
        fields.forEach(f => {
          const srcCol = mapping[f.key];
          if (!srcCol) return;
          const val = row[srcCol];
          obj[f.key] = numericFields.includes(f.key) ? (parseFloat(String(val).replace(/,/g, '')) || 0) : (val?.trim?.() || '');
        });
        if (type === 'broker') {
          obj.netShares = (obj.buyVol || 0) - (obj.sellVol || 0);
        }
        return obj;
      })
      .filter(row => row.date && row.symbol);
    onImport(normalized);
    resetUpload();
  };

  const resetUpload = () => {
    setRawData(null);
    setHeaders([]);
    setMapping({});
    setPreview([]);
    setFileName('');
  };

  const requiredMapped = fields.filter(f => f.required).every(f => mapping[f.key]);

  if (!rawData) {
    return (
      <div
        data-testid="csv-dropzone"
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-trading-border hover:border-trading-blue/50 rounded-sm p-12 text-center cursor-pointer transition-colors duration-150 bg-trading-surface/50"
      >
        <Upload className="w-8 h-8 text-trading-muted mx-auto mb-3" />
        <p className="text-sm text-trading-muted">Click to upload {label} CSV</p>
        <p className="text-xs text-trading-muted/60 mt-1">Supports COL Financial, Philstocks, and custom formats</p>
        <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} className="hidden" data-testid="csv-upload-input" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* File info */}
      <div className="flex items-center justify-between bg-trading-surface border border-trading-border p-3 rounded-sm">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-trading-blue" />
          <span className="text-sm font-mono text-trading-text">{fileName}</span>
          <span className="text-xs text-trading-muted">{rawData.length} rows, {headers.length} columns</span>
        </div>
        <Button variant="ghost" size="sm" onClick={resetUpload} data-testid="reset-upload-btn" className="text-trading-muted hover:text-trading-text">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Column Mapping */}
      <div className="bg-trading-surface border border-trading-border rounded-sm p-4">
        <h3 className="text-sm font-medium text-trading-text mb-3 flex items-center gap-2">
          Column Mapping
          {requiredMapped ? <Check className="w-4 h-4 text-trading-green" /> : <AlertTriangle className="w-4 h-4 text-trading-amber" />}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {fields.map(f => (
            <div key={f.key}>
              <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">
                {f.label} {f.required && <span className="text-trading-red">*</span>}
              </label>
              <Select value={mapping[f.key] || ''} onValueChange={(v) => setMapping(prev => ({ ...prev, [f.key]: v }))}>
                <SelectTrigger data-testid={`map-${f.key}`} className="h-8 bg-trading-bg border-trading-border text-sm text-trading-text">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent className="bg-trading-surface border-trading-border">
                  {headers.map(h => (
                    <SelectItem key={h} value={h} className="text-sm text-trading-text hover:bg-trading-elevated">{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      {preview.length > 0 && (
        <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden">
          <div className="px-4 py-2 border-b border-trading-border">
            <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">Preview (first 5 rows)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr className="bg-trading-elevated border-b border-trading-border">
                  {headers.slice(0, 8).map(h => <th key={h} className="text-left">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} className="border-b border-trading-border/50 hover:bg-trading-elevated/30">
                    {headers.slice(0, 8).map(h => <td key={h} className="text-trading-text/80">{row[h]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={handleConfirm} disabled={!requiredMapped} data-testid="confirm-upload-btn" className="bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm">
          <Check className="w-4 h-4 mr-2" /> Import {rawData.length} rows
        </Button>
        <Button variant="ghost" onClick={resetUpload} className="text-trading-muted hover:text-trading-text border border-trading-border rounded-sm">
          Cancel
        </Button>
      </div>
    </div>
  );
}

function DataSummaryCard({ title, count, symbols, onClear, testId }) {
  return (
    <div data-testid={testId} className="bg-trading-surface border border-trading-border rounded-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">{title}</span>
        {count > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear} className="text-trading-red/70 hover:text-trading-red text-xs h-6 px-2">Clear</Button>
        )}
      </div>
      <div className="flex gap-6">
        <div>
          <span className="font-mono text-xl text-trading-text tabular-nums">{count.toLocaleString()}</span>
          <span className="text-xs text-trading-muted ml-1">rows</span>
        </div>
        <div>
          <span className="font-mono text-xl text-trading-text tabular-nums">{symbols}</span>
          <span className="text-xs text-trading-muted ml-1">symbols</span>
        </div>
      </div>
    </div>
  );
}
